'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import BigArrow from '@/fo/icons/base/icons/big-arrow.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import {
  ReviewsData,
  QuestionsData,
  Question,
  Review
} from '@/fo/ui/review/catalog/base';
import { ReviewImage } from '@/fo/ui/review/gallery/base';
import { UiReviewInformerPoints } from '@/fo/ui/review/informer/points';
import { UiReviewLightboxBase } from '@/fo/ui/review/lightbox/base';
import { ReviewTab, UiReviewTabsBase } from '@/fo/ui/review/tabs/base';

import { ReviewsHeader, ReviewsCarousel } from '../components';

import { styles } from './UiReviewSectionBase.styles';

export interface UiReviewSectionBaseProps
  extends React.HTMLAttributes<HTMLDivElement> {
  reviewsData: ReviewsData;
  questionsData: QuestionsData;
  onActionButtonClick?: (type: ReviewTab) => void;
  onViewAllClick?: (type: ReviewTab) => void;
  onImageClick?: (imageId: string) => void;
  onReviewClick?: (review: Review) => void;
  onQuestionClick?: (question: Question) => void;
  isNewProduct?: boolean;
}

export function UiReviewSectionBase({
  reviewsData,
  questionsData,
  onActionButtonClick,
  onViewAllClick,
  onImageClick,
  onReviewClick,
  onQuestionClick,
  isNewProduct,
  ...props
}: UiReviewSectionBaseProps) {
  const t = useTranslations();

  const slots = styles();

  const [activeTab, setActiveTab] = useState(ReviewTab.Reviews);

  const [lightboxImageId, setLightboxImageId] = useState<string | null>(null);
  const [lightboxImages, setLightboxImages] = useState<ReviewImage[]>([]);

  const tabs = [
    {
      id: ReviewTab.Reviews,
      title: t('review.tabs.reviews'),
      emptyTitle: t('review.empty.reviews'),
      count: reviewsData.totalCount
    },
    {
      id: ReviewTab.Questions,
      title: t('review.tabs.questions'),
      emptyTitle: t('review.empty.questions'),
      count: questionsData.totalCount
    }
  ];

  const isReviewsTab = activeTab === ReviewTab.Reviews;
  const isEmpty = isReviewsTab
    ? reviewsData.items.length === 0
    : questionsData.items.length === 0;

  const handleImageClick = (imageId: string, images: ReviewImage[]) => {
    setLightboxImageId(imageId);
    setLightboxImages(images);
  };

  const buttonText = isReviewsTab
    ? t('review.buttons.leaveReview')
    : t('review.buttons.askQuestion');

  return (
    <>
      <section
        className={slots.base()}
        {...props}
      >
        <div className={slots.header()}>
          <div className={slots.tabsContainer()}>
            <UiReviewTabsBase
              tabs={tabs}
              activeTabId={activeTab}
              onTabChange={(tabId) => setActiveTab(tabId)}
            />
            {!isEmpty && (
              <button
                type="button"
                className={slots.mobileButton()}
                onClick={() => onViewAllClick?.(activeTab)}
              >
                <BigArrow />
              </button>
            )}
          </div>

          {isReviewsTab && (
            <UiReviewInformerPoints
              mode="list"
              isNewProduct={isNewProduct}
              className={slots.informer()}
            />
          )}

          {isReviewsTab && !isEmpty && (
            <ReviewsHeader
              className={slots.reviewsHeader()}
              reviewsData={reviewsData}
              onImageClick={onImageClick}
            />
          )}

          <UiKitButtonBase
            className={slots.button()}
            onClick={() => onActionButtonClick?.(activeTab)}
          >
            {buttonText}
          </UiKitButtonBase>
        </div>

        {!isEmpty && (
          <>
            <div className={slots.contentWrapper()}>
              <ReviewsCarousel
                items={isReviewsTab ? reviewsData.items : questionsData.items}
                type={activeTab}
                onReviewClick={onReviewClick}
                onQuestionClick={onQuestionClick}
                onImageClick={handleImageClick}
              />
            </div>

            <button
              className={slots.footerButton()}
              onClick={() => onViewAllClick?.(activeTab)}
              type="button"
            >
              {isReviewsTab
                ? t('review.buttons.viewAllReviews')
                : t('review.buttons.viewAllQuestions')}

              <BigArrow />
            </button>
          </>
        )}
      </section>
      <UiReviewLightboxBase
        images={lightboxImages.map((image) => ({
          id: image.id,
          src: image.url,
          alt: image.alt
        }))}
        title="Review images"
        open={!!lightboxImageId}
        onOpenChange={(open) => {
          if (!open) {
            setLightboxImageId(null);
          }
        }}
      />
    </>
  );
}
